import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtINR, fmtDate } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function EventDetail() {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [regs, setRegs] = useState([]);
  const [regOpen, setRegOpen] = useState(false);
  const [regForm, setRegForm] = useState({
    team_name: "", captain_name: "", players_count: 0, phone: "", email: "",
    amount: 0, payment_status: "Pending", notes: "",
  });

  const load = async () => {
    const [e, t, s, r, ex, rg] = await Promise.all([
      api.get(`/events/${id}`),
      api.get("/tasks"), api.get("/sponsors"),
      api.get("/revenue"), api.get("/expenses"), api.get("/registrations"),
    ]);
    setEvent(e.data);
    setTasks(t.data.filter((x) => x.event_id === id));
    setSponsors(s.data.filter((x) => x.event_id === id));
    setRevenue(r.data.filter((x) => x.event_id === id));
    setExpenses(ex.data.filter((x) => x.event_id === id));
    setRegs(rg.data.filter((x) => x.event_id === id));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!event) return <div className="text-slate-500">Loading event...</div>;

  const saveReg = async () => {
    if (!regForm.team_name.trim()) return toast.error("Team name required");
    try {
      await api.post("/registrations", { ...regForm, event_id: id, event_name: event.name });
      toast.success("Registration added");
      setRegOpen(false);
      setRegForm({ team_name: "", captain_name: "", players_count: 0, phone: "", email: "", amount: 0, payment_status: "Pending", notes: "" });
      load();
    } catch { toast.error("Failed"); }
  };

  const totalTeams = regs.length;
  const totalPlayers = regs.reduce((s, r) => s + Number(r.players_count || 0), 0);
  const regRev = regs.filter((r) => r.payment_status === "Paid").reduce((s, r) => s + Number(r.amount || 0), 0);
  const regPending = regs.filter((r) => r.payment_status !== "Paid").reduce((s, r) => s + Number(r.amount || 0), 0);
  const sponsorRev = sponsors.reduce((s, x) => s + Number(x.final_value || 0), 0);
  const otherRev = revenue.reduce((s, x) => s + Number(x.amount || 0), 0);
  const totalRev = regRev + sponsorRev + otherRev;
  const totalCost = expenses.reduce((s, x) => s + Number(x.amount || 0), 0);
  const profit = totalRev - totalCost;
  const margin = totalRev ? (profit / totalRev) * 100 : 0;

  const TASK_GROUPS = ["Operations", "Marketing", "Sponsorship", "Registrations", "Vendors", "Officials", "Branding", "Photography / Video", "Venue Preparation"];

  return (
    <div className="space-y-6" data-testid="event-detail-page">
      <div>
        <Link to="/events" className="text-xs text-blue-600 hover:underline">← Back to events</Link>
        <div className="flex justify-between items-start flex-wrap gap-3 mt-2">
          <div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>{event.name}</h1>
            <div className="text-sm text-slate-500 mt-1">{event.type} · {event.venue_name} · {fmtDate(event.date)}</div>
          </div>
          <StatusBadge status={event.status} />
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="tasks">Task Board</TabsTrigger>
          <TabsTrigger value="registrations">Registrations</TabsTrigger>
          <TabsTrigger value="sponsors">Sponsors</TabsTrigger>
          <TabsTrigger value="finance">Finance</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid md:grid-cols-4 gap-4">
            {[
              ["Revenue Target", fmtINR(event.revenue_target)],
              ["Budget", fmtINR(event.budget)],
              ["Expected Participants", event.expected_participants || 0],
              ["Expected Footfall", event.expected_footfall || 0],
              ["Reg Fee", fmtINR(event.registration_fee)],
              ["Owner", event.owner_name || "—"],
              ["Start", event.start_time || "—"],
              ["End", event.end_time || "—"],
            ].map(([l, v]) => (
              <Card key={l} className="border-slate-200"><CardContent className="p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">{l}</div>
                <div className="text-lg font-black mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>{v}</div>
              </CardContent></Card>
            ))}
          </div>
          {event.notes && <Card className="border-slate-200 mt-4"><CardContent className="p-6"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Notes</div><div className="text-sm text-slate-700">{event.notes}</div></CardContent></Card>}
        </TabsContent>

        <TabsContent value="tasks">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {TASK_GROUPS.map((g) => {
              const gt = tasks.filter((t) => t.department === g || t.title.toLowerCase().includes(g.toLowerCase()));
              return (
                <Card key={g} className="border-slate-200"><CardContent className="p-4">
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">{g} · {gt.length}</div>
                  <ul className="space-y-2">
                    {gt.length === 0 && <li className="text-xs text-slate-400">No tasks</li>}
                    {gt.map((t) => (
                      <li key={t.id} className="text-sm border-l-2 border-blue-500 pl-2">
                        <div className="font-medium">{t.title}</div>
                        <div className="text-xs text-slate-500">{t.assignee_name} · <StatusBadge status={t.status} /></div>
                      </li>
                    ))}
                  </ul>
                </CardContent></Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="registrations">
          <div className="space-y-4">
            <div className="grid md:grid-cols-4 gap-4">
              <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Teams</div><div className="text-2xl font-black mt-1">{totalTeams}</div></CardContent></Card>
              <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Players</div><div className="text-2xl font-black mt-1">{totalPlayers}</div></CardContent></Card>
              <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Reg. Revenue (Paid)</div><div className="text-2xl font-black mt-1 text-emerald-700">{fmtINR(regRev)}</div></CardContent></Card>
              <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Pending Fees</div><div className="text-2xl font-black mt-1 text-amber-700">{fmtINR(regPending)}</div></CardContent></Card>
            </div>
            <div className="flex justify-end">
              <Dialog open={regOpen} onOpenChange={setRegOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-registration-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add Registration</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>New Team Registration</DialogTitle></DialogHeader>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2"><Label>Team Name</Label><Input data-testid="reg-team-input" value={regForm.team_name} onChange={(e) => setRegForm({ ...regForm, team_name: e.target.value })} /></div>
                    <div><Label>Captain</Label><Input value={regForm.captain_name} onChange={(e) => setRegForm({ ...regForm, captain_name: e.target.value })} /></div>
                    <div><Label>Players</Label><Input type="number" value={regForm.players_count} onChange={(e) => setRegForm({ ...regForm, players_count: +e.target.value })} /></div>
                    <div><Label>Phone</Label><Input value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} /></div>
                    <div><Label>Email</Label><Input value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} /></div>
                    <div><Label>Fee Amount (₹)</Label><Input type="number" value={regForm.amount} onChange={(e) => setRegForm({ ...regForm, amount: +e.target.value })} /></div>
                    <div><Label>Payment</Label>
                      <Select value={regForm.payment_status} onValueChange={(v) => setRegForm({ ...regForm, payment_status: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Pending">Pending</SelectItem>
                          <SelectItem value="Paid">Paid</SelectItem>
                          <SelectItem value="Refunded">Refunded</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <DialogFooter><Button variant="outline" onClick={() => setRegOpen(false)}>Cancel</Button><Button data-testid="reg-save-button" onClick={saveReg} className="bg-blue-600 hover:bg-blue-700">Save</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            <Card className="border-slate-200"><Table>
              <TableHeader><TableRow><TableHead>Team</TableHead><TableHead>Captain</TableHead><TableHead>Players</TableHead><TableHead>Contact</TableHead><TableHead className="text-right">Fee</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {regs.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">No registrations yet. Add your first team above.</TableCell></TableRow>}
                {regs.map((r) => (
                  <TableRow key={r.id} data-testid={`reg-row-${r.id}`}>
                    <TableCell className="font-medium">{r.team_name}</TableCell>
                    <TableCell>{r.captain_name}</TableCell>
                    <TableCell>{r.players_count}</TableCell>
                    <TableCell className="text-xs text-slate-500">{r.phone || r.email || "—"}</TableCell>
                    <TableCell className="text-right font-mono">{fmtINR(r.amount)}</TableCell>
                    <TableCell><StatusBadge status={r.payment_status} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></Card>
          </div>
        </TabsContent>

        <TabsContent value="sponsors">
          <Card className="border-slate-200"><Table>
            <TableHeader><TableRow><TableHead>Brand</TableHead><TableHead>Category</TableHead><TableHead>Contact</TableHead><TableHead className="text-right">Expected</TableHead><TableHead className="text-right">Final</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
            <TableBody>
              {sponsors.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-6">No sponsors linked to this event.</TableCell></TableRow>}
              {sponsors.map((s) => (<TableRow key={s.id}><TableCell className="font-medium">{s.brand}</TableCell><TableCell>{s.category}</TableCell><TableCell>{s.contact_name}</TableCell><TableCell className="text-right font-mono">{fmtINR(s.expected_value)}</TableCell><TableCell className="text-right font-mono">{fmtINR(s.final_value)}</TableCell><TableCell><StatusBadge status={s.status} /></TableCell></TableRow>))}
            </TableBody>
          </Table></Card>
        </TabsContent>

        <TabsContent value="finance">
          <div className="grid md:grid-cols-4 gap-4">
            <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Total Revenue</div><div className="text-xl font-black text-emerald-700 mt-1">{fmtINR(totalRev)}</div></CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Total Cost</div><div className="text-xl font-black text-rose-700 mt-1">{fmtINR(totalCost)}</div></CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Profit</div><div className={`text-xl font-black mt-1 ${profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtINR(profit)}</div></CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Margin</div><div className="text-xl font-black mt-1">{margin.toFixed(1)}%</div></CardContent></Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
